import { inject, injectable } from "tsyringe";
import { Observable, timer } from "rxjs";
import { Log } from "src/libs/log";
import { map, tap } from "rxjs/operators";
import { SurveyStorageKeys } from "src/containers/surveys/constants/storage";
import { SurveyProperties } from "src/containers/surveys/constants/properties";
import { SurveyAnswer } from "src/shared/models/survey-answer.model";
import { Survey } from "src/shared/models/survey.model";
import { GenericSuccessResponse } from "src/infra/http/types";
import { autoBind } from "src/shared/decorators/autobind";
import { SurveysRepository } from "src/containers/surveys/repositories/survey/surveys-repository";

@injectable()
export class SurveysService {
    constructor(@inject("SurveysRepository") private repo: SurveysRepository) {}

    @autoBind
    getAnsweredSurveys(): Observable<Survey[]> {
        return this.repo
            .getAnsweredSurveys()
            .pipe(
                map((surveys) =>
                    surveys.map((survey) => Survey.fromJson<Survey>(survey))
                )
            );
    }

    @autoBind
    answerSurvey(payload: SurveyAnswer): Observable<GenericSuccessResponse> {
        return this.repo.answerSurvey(payload).pipe(
            tap(() => {
                this.recordAnswer();
            })
        );
    }

    @autoBind
    omitSurvey(): void {
        localStorage.setItem(SurveyStorageKeys.OmitSurvey, "true");
    }

    @autoBind
    isOmitted(): boolean {
        const isOmitted = localStorage.getItem(SurveyStorageKeys.OmitSurvey);
        return isOmitted === "true";
    }

    @autoBind
    recordAnswer(): void {
        localStorage.setItem(SurveyStorageKeys.AnswerSurvey, "true");
    }

    @autoBind
    isAnswered(): boolean {
        return localStorage.getItem(SurveyStorageKeys.AnswerSurvey) === "true";
    }

    @autoBind
    recordTransaction() {
        Log.info("Recording survey transaction");
        return this.createTransaction().pipe(
            tap(() => {
                const currentTxs = this.getTransactions();
                localStorage.setItem(
                    SurveyStorageKeys.Transactions,
                    String(currentTxs + 1)
                );
            })
        );
    }

    @autoBind
    getTransactions(): number {
        const currentTxs = localStorage.getItem(SurveyStorageKeys.Transactions);
        return Number.parseInt(currentTxs ?? "0", 10);
    }

    @autoBind
    clear(): void {
        localStorage.removeItem(SurveyStorageKeys.Transactions);
        localStorage.removeItem(SurveyStorageKeys.OmitSurvey);
        localStorage.removeItem(SurveyStorageKeys.AnswerSurvey);
    }

    @autoBind
    resetTransactions(): void {
        localStorage.setItem(SurveyStorageKeys.Transactions, "0");
    }

    @autoBind
    private createTransaction() {
        return timer(SurveyProperties.TransactionTimeout);
    }
}
